import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RotateCcw, ChevronRight } from 'lucide-react';

const PHASES = [
  { label: 'Connecting', threshold: 15 },
  { label: 'Sending data', threshold: 35 },
  { label: 'Processing', threshold: 60 },
  { label: 'Running steps', threshold: 80 },
  { label: 'Finalizing', threshold: 95 },
];

const STATUS_MESSAGES = [
  'Connecting to automation...',
  'Sending your data...',
  'Processing information...',
  'Running workflow steps...',
  'Generating results...',
  'Almost there...',
];

/**
 * Polished flow execution screen with progress illusion & rotating status messages.
 */
export default function FlowExecutionScreen({ isComplete, error, customMessage, onRetry, onSkip, compact }) {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const startTime = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  // Decelerating fake progress
  useEffect(() => {
    if (isComplete || error) return;
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return prev + 0.02;  // near-stop crawl while waiting
        if (prev >= 80) return prev + 0.05;  // slow crawl
        if (prev >= 60) return prev + 0.8;   // moderate
        if (prev >= 35) return prev + 2;     // fast
        return prev + 4;                      // blast through early phases
      });
    }, 100);
    return () => clearInterval(interval);
  }, [isComplete, error]);

  // Snap to 100% on complete
  useEffect(() => {
    if (isComplete) setProgress(100);
  }, [isComplete]);

  // Rotate status messages every 3s
  useEffect(() => {
    if (isComplete || error) return;
    const interval = setInterval(() => {
      setMessageIndex(prev => (prev + 1) % STATUS_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isComplete, error]);

  // Elapsed timer
  useEffect(() => {
    if (isComplete || error) return;
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTime.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [isComplete, error]);

  const formatElapsed = (s) => {
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${s % 60}s`;
  };

  const currentPhase = PHASES.findIndex(p => progress < p.threshold);
  const phaseIndex = currentPhase === -1 ? PHASES.length - 1 : currentPhase;

  const displayMessage = error
    ? (typeof error === 'string' ? error : 'The automation encountered an error')
    : isComplete
    ? 'Automation complete!'
    : customMessage || STATUS_MESSAGES[messageIndex];

  return (
    <div className={`flex flex-col items-center justify-center text-center ${compact ? 'space-y-4 py-6' : 'space-y-5 py-10'}`}>
      {/* Status text */}
      <div className={compact ? 'space-y-1 px-4' : 'space-y-2'}>
        {error && <AlertCircle className="h-5 w-5 text-red-500 mx-auto mb-2" />}
        <p className={`font-semibold transition-opacity duration-300 ${compact ? 'text-base' : 'text-lg'}`}>
          {displayMessage}
        </p>
        {!error && !isComplete && (
          <p className="text-muted-foreground text-sm tabular-nums">
            {formatElapsed(elapsed)}
          </p>
        )}
        {isComplete && !error && (
          <p className="text-muted-foreground text-sm">Moving to next step...</p>
        )}
      </div>

      {/* Phase steps (only while running, not compact) */}
      {!error && !isComplete && !compact && (
        <div className="flex items-center gap-2">
          {PHASES.map((phase, i) => (
            <div key={phase.label} className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 text-xs transition-colors duration-300 ${
                i <= phaseIndex ? 'text-foreground font-medium' : 'text-muted-foreground/50'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
                  i < phaseIndex
                    ? 'bg-foreground'
                    : i === phaseIndex
                    ? 'bg-foreground animate-pulse'
                    : 'bg-muted-foreground/30'
                }`} />
                {phase.label}
              </div>
              {i < PHASES.length - 1 && (
                <div className={`w-4 h-px transition-colors duration-300 ${
                  i < phaseIndex ? 'bg-foreground' : 'bg-muted-foreground/20'
                }`} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Linear progress bar */}
      <div className={`w-full ${compact ? 'max-w-[200px]' : 'max-w-xs'}`}>
        <div className={`w-full bg-muted rounded-full overflow-hidden ${compact ? 'h-1' : 'h-1.5'}`}>
          <div
            className={`h-full rounded-full transition-all ${
              error
                ? 'bg-red-500'
                : isComplete
                ? 'bg-emerald-500 duration-600'
                : 'bg-foreground duration-300'
            }`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className={`w-full ${compact ? 'max-w-[280px] space-y-3' : 'max-w-sm space-y-4'}`}>
          {typeof error === 'object' && (
            <pre className={`text-left p-3 rounded-lg font-mono overflow-auto bg-red-50 border border-red-200 text-red-800 ${
              compact ? 'text-[10px] max-h-[100px]' : 'text-xs max-h-[160px]'
            }`}>
              {JSON.stringify(error, null, 2)}
            </pre>
          )}
          <div className="flex items-center justify-center gap-2">
            {onRetry && (
              <Button size={compact ? 'sm' : 'default'} onClick={onRetry}>
                <RotateCcw className={`${compact ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                Retry
              </Button>
            )}
            {onSkip && (
              <Button variant="outline" size={compact ? 'sm' : 'default'} onClick={onSkip}>
                Skip
                <ChevronRight className={`${compact ? 'h-3 w-3 ml-0.5' : 'h-4 w-4 ml-1'}`} />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
